import React, { useContext, useRef, useState } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { API_ENDPOINT } from '../../../sharedModules/utils/env';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';

/** Rails often returns `/rails/active_storage/...`; without the API origin the browser hits the SPA host and 404s. */
function apiAbsoluteUrl(pathOrAbsolute: string): string {
  if (/^https?:\/\//i.test(pathOrAbsolute)) return pathOrAbsolute;
  const base = API_ENDPOINT.replace(/\/$/, '');
  const path = pathOrAbsolute.startsWith('/') ? pathOrAbsolute : `/${pathOrAbsolute}`;
  return `${base}${path}`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  company: string;
  documents: CollectionInvoiceDocument[];
  location: string;
  acctNo: string;
  onInvoicePdfUploaded?: (applyTo: string, pdf: CollectionInvoicePdf, acctNo: string) => void;
}

function formatOptionalDate(isoLike: string | null): string {
  if (!isoLike) return '—';
  try {
    const d = parseISO(isoLike);
    return isValid(d) ? format(d, 'MMM d, yyyy') : isoLike;
  } catch {
    return isoLike;
  }
}

const InvoicePdfsModal: React.FC<Props> = ({
  open,
  onClose,
  company,
  documents,
  location,
  acctNo,
  onInvoicePdfUploaded,
}) => {
  const { showErrorSnackBar } = useContext(SnackBarContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingApplyToRef = useRef<string | null>(null);
  const [uploadingApplyTo, setUploadingApplyTo] = useState<string | null>(null);

  const matched = documents.filter((d) => d.has_pdf).length;
  const total = documents.length;
  const canUpload = Boolean(location && acctNo);

  const triggerUpload = (applyTo: string) => {
    pendingApplyToRef.current = applyTo;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const applyTo = pendingApplyToRef.current;
    e.target.value = '';
    pendingApplyToRef.current = null;
    if (!file || !applyTo || !canUpload) return;

    setUploadingApplyTo(applyTo);
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('location', location);
    formData.append('acct_no', acctNo);
    formData.append('apply_to', applyTo);

    try {
      const res = await axios.post('/collections/edoc_invoice_imports', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const payload = res.data as unknown as { pdf: CollectionInvoicePdf };
      onInvoicePdfUploaded?.(applyTo, payload.pdf, acctNo);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to upload PDF';
      showErrorSnackBar?.(msg);
    } finally {
      setUploadingApplyTo(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFileSelected} />
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Invoices — {company}</Typography>
          <Typography variant="body2" color="textSecondary">
            {matched} of {total} {total === 1 ? 'invoice has a PDF' : 'invoices have PDFs'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        {documents.length === 0 ? (
          <Box sx={{ p: 3, color: 'text.secondary' }}>
            <Typography variant="body2">No past-due invoices.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>PDF</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.apply_to} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{doc.apply_to}</TableCell>
                    <TableCell>{formatOptionalDate(doc.invoice_date)}</TableCell>
                    <TableCell>{formatOptionalDate(doc.due_date)}</TableCell>
                    <TableCell align="right">${doc.amount.toFixed(2)}</TableCell>
                    <TableCell>
                      {doc.pdf ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PictureAsPdfIcon sx={{ color: '#e53935', fontSize: 18, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {doc.pdf.filename}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" color="textSecondary">
                            No PDF available
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            disabled={!canUpload || uploadingApplyTo === doc.apply_to}
                            onClick={() => triggerUpload(doc.apply_to)}
                          >
                            {uploadingApplyTo === doc.apply_to ? 'Uploading…' : 'Upload PDF'}
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {doc.pdf?.download_url ? (
                        <Button
                          component="a"
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          href={apiAbsoluteUrl(doc.pdf.download_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={doc.pdf.filename}
                        >
                          Download
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoicePdfsModal;
